/**
 * Modal Utilities
 *
 * This file provides utility functions for displaying modals using Ant Design's App component.
 * These functions help avoid the "Static function can not consume context like dynamic theme" warning.
 */
import type { ModalFuncProps } from 'antd/es/modal/interface';
import type { ModalStaticFunctions } from 'antd/es/modal/confirm';

// Modal instance from the App component
let modal: Partial<ModalStaticFunctions> | null = null;

/**
 * Initialize the modal instance
 * This should be called by the NotificationProvider component
 */
export const initModalInstance = (modalInstance: Partial<ModalStaticFunctions> | null) => {
  modal = modalInstance;
};

/**
 * Show a confirmation modal
 * @param options - The modal options
 */
export const showConfirmModal = (options: ModalFuncProps) => {
  if (modal && modal.confirm) {
    return modal.confirm(options);
  }
  console.warn('Modal instance not initialized or confirm method not available');
  return null;
};

/**
 * Show an info modal
 * @param options - The modal options
 */
export const showInfoModal = (options: ModalFuncProps) => {
  if (modal && modal.info) {
    return modal.info(options);
  }
  console.warn('Modal instance not initialized or info method not available');
  return null;
};

/**
 * Show a success modal
 * @param options - The modal options
 */
export const showSuccessModal = (options: ModalFuncProps) => {
  if (modal && modal.success) {
    return modal.success(options);
  }
  console.warn('Modal instance not initialized or success method not available');
  return null;
};

/**
 * Show an error modal
 * @param options - The modal options
 */
export const showErrorModal = (options: ModalFuncProps) => {
  if (modal && modal.error) {
    return modal.error(options);
  }
  console.warn('Modal instance not initialized or error method not available');
  return null;
};

/**
 * Show a warning modal
 * @param options - The modal options
 */
export const showWarningModal = (options: ModalFuncProps) => {
  if (modal && modal.warning) {
    return modal.warning(options);
  }
  console.warn('Modal instance not initialized or warning method not available');
  return null;
};
